import { supabase } from './supabase.ts';
import { Property, PropertyFormData } from '../types/property.ts';

export const propertyService = {
  async fetchProperties() {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Error fetching properties: ${error.message}`);
    }

    // Fetch tenants separately for properties that are rented
    const rentedProperties = data?.filter(p => p.status === 'Rented') || [];
    if (rentedProperties.length > 0) {
      const { data: tenants } = await supabase
        .from('tenants')
        .select('*')
        .in('property_id', rentedProperties.map(p => p.id))
        .eq('status', 'Active');

      // Merge tenant data with properties
      return data?.map(property => ({
        ...property,
        tenant: tenants?.find(t => t.property_id === property.id) || null
      }));
    }

    return data;
  },

  // Fetch only rented properties (for payment form)
  async fetchRentedProperties() {
    const { data, error } = await supabase
      .from('properties')
      .select('*')
      .eq('status', 'Rented');

    if (error) {
      throw new Error(`Error fetching rented properties: ${error.message}`);
    }

    return data as Property[];
  },

  async getPropertyById(id: string) {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        tenants (
          id,
          full_name,
          monthly_rent,
          start_date,
          end_date,
          status
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      throw new Error(`Error fetching property: ${error.message}`);
    }

    return data as Property;
  },

  async createProperty(propertyData: PropertyFormData) {
    const { data, error } = await supabase
      .from('properties')
      .insert([propertyData])
      .select()
      .single();

    if (error) {
      throw new Error(`Error creating property: ${error.message}`);
    }

    return data as Property;
  },

  async updateProperty(id: string, propertyData: PropertyFormData) {
    // First get current property status with LEFT JOIN to handle no tenants case
    const { data: currentProperty, error: fetchError } = await supabase
      .from('properties')
      .select(`
        status,
        tenants (
          id, 
          status
        )
      `)
      .eq('id', id)
      .maybeSingle(); // Use maybeSingle instead of single

    if (fetchError) {
      throw new Error(`Error fetching property: ${fetchError.message}`);
    }

    // If property has active tenant, preserve Rented status
    const hasActiveTenant = currentProperty?.tenants?.some(
      (t: any) => t.status === 'Active'
    );

    const finalData = {
      ...propertyData,
      status: hasActiveTenant ? 'Rented' : propertyData.status
    };

    const { data, error } = await supabase
      .from('properties')
      .update(finalData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Error updating property: ${error.message}`);
    }

    return data as Property;
  },

  async deleteProperty(id: string) {
    const { error } = await supabase
      .from('properties')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`Error deleting property: ${error.message}`);
    }
  }
};
